import {
  PosterProject,
  GalleryCell,
  GalleryCellItem,
  Viewport,
} from "./poster-project";
import { ProjectTemplate } from "./project-template";
import { defaultSizeOption, defaultGalleryEditingPermissions } from "./default-project-style";

export const migrateProject = (project: PosterProject): PosterProject => {
  let migrated = migrateSizeOption(project);
  migrated = migrateEditingPermissions(migrated);
  migrated = migrateViewportContentOffsetAndScale(migrated);
  migrated = migrateGalleryBackgroundAndGapOptions(migrated);
  migrated = migrateGalleryVisibility(migrated);
  return migrated;
};

const migrateSizeOption = (project: PosterProject): PosterProject => {
  let migrated = { ...project };

  if (migrated.sizeOption === undefined) {
    migrated.sizeOption = defaultSizeOption;
  }

  return migrated;
};

/**
 * Migration: Ensure editingPermissions.gallery exists.
 * Older projects created before gallery feature was added don't have this property.
 */
const migrateEditingPermissions = (project: PosterProject): PosterProject => {
  if (project.editingPermissions?.gallery) {
    return project;
  }

  return {
    ...project,
    editingPermissions: {
      ...project.editingPermissions,
      gallery: defaultGalleryEditingPermissions,
    },
  };
};

/**
 * Migration for model version 13: Add contentOffset and contentScale to viewports.
 * Sets default values for existing viewports that don't have these properties.
 */
const migrateViewportContentOffsetAndScale = (
  project: PosterProject
): PosterProject => {
  if (!project.gallery) {
    return project;
  }

  const migrateViewport = (viewport: Viewport): Viewport => {
    return {
      ...viewport,
      contentOffset: viewport.contentOffset ?? { x: 0, y: 0 },
      contentScale: viewport.contentScale ?? 1.0,
    };
  };

  const migrateCellItem = (item: GalleryCellItem): GalleryCellItem => {
    if (item.type === "viewport") {
      return { type: "viewport", viewport: migrateViewport(item.viewport) };
    } else {
      return { type: "cell", cell: migrateCell(item.cell) };
    }
  };

  const migrateCell = (cell: GalleryCell): GalleryCell => {
    return {
      ...cell,
      items: cell.items.map(migrateCellItem),
    };
  };

  return {
    ...project,
    gallery: {
      ...project.gallery,
      content: project.gallery.content.map(migrateCell),
    },
  };
};

/**
 * Migration for model version 14: Add gapAroundEdges, gapTowardMap, backgroundColor,
 * and backgroundOpacity to galleries.
 * Sets default values for existing galleries that don't have these properties.
 */
const migrateGalleryBackgroundAndGapOptions = (
  project: PosterProject
): PosterProject => {
  if (!project.gallery) {
    return project;
  }

  // Check if migration is needed (if any of the new properties are undefined)
  const gallery = project.gallery;
  if (
    gallery.gapAroundEdges !== undefined &&
    gallery.gapTowardMap !== undefined &&
    gallery.backgroundColor !== undefined &&
    gallery.backgroundOpacity !== undefined
  ) {
    return project;
  }

  return {
    ...project,
    gallery: {
      ...gallery,
      gapAroundEdges: gallery.gapAroundEdges ?? false,
      gapTowardMap: gallery.gapTowardMap ?? true,
      backgroundColor: gallery.backgroundColor ?? "#FFFFFF",
      backgroundOpacity: gallery.backgroundOpacity ?? 0,
    },
  };
};

/**
 * Migration: Add isVisible property to galleries.
 * Existing galleries should default to visible to maintain backward compatibility.
 */
const migrateGalleryVisibility = (project: PosterProject): PosterProject => {
  if (!project.gallery) {
    return project;
  }

  // Check if migration is needed
  if (project.gallery.isVisible !== undefined) {
    return project;
  }

  return {
    ...project,
    gallery: {
      ...project.gallery,
      isVisible: true, // Existing galleries default to visible
    },
  };
};

/**
 * Migrates a ProjectTemplate to ensure appliedElements.gallery is set correctly.
 * If the template's project has a gallery but appliedElements.gallery is false,
 * it sets gallery to true so the gallery gets copied to new projects.
 *
 * This handles templates created before the gallery checkbox was added to the UI,
 * where gallery defaulted to false even though the template had a gallery.
 */
export const migrateTemplate = (template: ProjectTemplate): ProjectTemplate => {
  const hasGallery = template.project.gallery !== undefined;
  const galleryApplied = template.appliedElements.gallery === true;

  // If the template has a gallery but it's not set to be applied, enable it
  if (hasGallery && !galleryApplied) {
    return {
      ...template,
      appliedElements: {
        ...template.appliedElements,
        gallery: true,
      },
    };
  }

  return template;
};
